import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Pagination from "components/pagination";
import { Toaster, toast } from "react-hot-toast";
import { apiCall } from "utils/apiClient";
import { FaEdit, FaEye, FaImage, FaPlus, FaSearch, FaTrash } from "react-icons/fa";

const PROJECT_TYPES = ["Residential", "Commercial", "Retail"];
const PAGE_SIZE = 10;
const API_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "http://localhost:8080/";
const isSuccess = (result) => result?.status >= 200 && result?.status < 300;
const mediaUrl = (path) => {
  if (!path || /^(https?:|blob:|data:)/i.test(path)) return path || "";
  return `${API_BASE_URL.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
};

function PortfolioManagement() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [filters, setFilters] = useState({ search: "", type: "", status: "" });
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(
    async (page = 1) => {
      setLoading(true);
      const params = { page: String(page), limit: String(PAGE_SIZE) };
      if (filters.search) params.search = filters.search;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      const result = await apiCall({ endpoint: "portfolio/projects", method: "GET", params });
      if (isSuccess(result)) {
        const payload = result.data || {};
        setProjects(Array.isArray(payload.projects) ? payload.projects : []);
        setPagination({
          page: payload.pagination?.page || page,
          total: payload.pagination?.total || 0,
          total_pages: payload.pagination?.total_pages || 1,
        });
      } else {
        toast.error(result?.message || "Unable to load portfolio projects.");
        setProjects([]);
      }
      setLoading(false);
    },
    [filters]
  );

  useEffect(() => {
    loadProjects(1);
  }, [loadProjects]);

  const deleteProject = async (project) => {
    if (!window.confirm(`Delete “${project.name}” and all its media files?`)) return;
    const result = await apiCall({
      endpoint: `portfolio/projects/${project.id}`,
      method: "DELETE",
    });
    if (!isSuccess(result)) {
      toast.error(result?.message || "Unable to delete project.");
      return;
    }
    toast.success("Portfolio project deleted.");
    loadProjects(
      projects.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" />
      <div className="py-3">
        <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Portfolio projects</h2>
              <p className="mt-1 text-xs text-slate-500">{pagination.total} total projects</p>
            </div>
            <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-[minmax(220px,1fr)_170px_170px_auto]">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  setFilters((current) => ({ ...current, search: searchInput.trim() }));
                }}
                className="relative"
              >
                <FaSearch className="absolute left-3 top-3 text-slate-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search name or location"
                  className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-500"
                />
              </form>
              <select
                value={filters.type}
                onChange={(event) => setFilters({ ...filters, type: event.target.value })}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm"
              >
                <option value="">All types</option>
                {PROJECT_TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
              <select
                value={filters.status}
                onChange={(event) => setFilters({ ...filters, status: event.target.value })}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button
                onClick={() => navigate("/portfolio-management/create")}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-indigo-700"
              >
                <FaPlus /> Add Project
              </button>
            </div>
          </div>

          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Project</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Media</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-slate-500">
                      Loading projects...
                    </td>
                  </tr>
                ) : projects.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-slate-500">
                      No portfolio projects found.
                    </td>
                  </tr>
                ) : (
                  projects.map((project) => (
                    <tr key={project.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {project.cover_media?.file_path &&
                          project.cover_media?.type !== "video" ? (
                            <img
                              src={mediaUrl(project.cover_media.file_path)}
                              alt=""
                              className="h-12 w-16 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="flex h-12 w-16 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                              <FaImage />
                            </span>
                          )}
                          <div>
                            <p className="font-bold text-slate-900">{project.name}</p>
                            <p className="mt-1 text-xs text-slate-400">ID #{project.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{project.location}</td>
                      <td className="px-4 py-3 text-slate-600">{project.type}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {project.image_count || 0} images · {project.video_count || 0} videos
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${
                            project.status === "active"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {project.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            title="View project and media"
                            onClick={() => navigate(`/portfolio-management/${project.id}`)}
                            className="rounded-lg bg-sky-50 p-2.5 text-sky-700 hover:bg-sky-100"
                          >
                            <FaEye />
                          </button>
                          <button
                            title="Edit project"
                            onClick={() => navigate(`/portfolio-management/${project.id}/edit`)}
                            className="rounded-lg bg-indigo-50 p-2.5 text-indigo-700 hover:bg-indigo-100"
                          >
                            <FaEdit />
                          </button>
                          <button
                            title="Delete project"
                            onClick={() => deleteProject(project)}
                            className="rounded-lg bg-red-50 p-2.5 text-red-700 hover:bg-red-100"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pagination.total_pages > 1 && (
            <div className="mt-5">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                onPageChange={loadProjects}
                recordsPerPage={PAGE_SIZE}
                onRecordsPerPageChange={() => {}}
                totalRecords={pagination.total}
                count={projects.length}
                compact
              />
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

export default PortfolioManagement;
